import React, { useEffect, useRef, useState } from 'react';
import { BaileyStill } from '../bailey/BaileyAvatar';
import { Icon } from '../BaileyBits';
import { hapticError, hapticSuccess, hapticTick } from '../../../_utils/haptics';
import SoulCards from './SoulCards';
import SoulDial, { SoulSegment } from './SoulDial';
import {
    SOULS, RULES, DIALS, DIAL_IDS, EMOJI, NOTES_MAX, fromPreset, normaliseSoul, isTuned, presetFor,
    compileSoul, soulSummary, soulHue, chatGreeting, clientReply, ownerReply, phoneGreeting
} from './soul';
import { errorText } from './api';
import VoicePanel, { VoiceLine } from './VoicePanel';

/**
 * Bailey's soul: pick a starting point, tune three dials, add house rules,
 * and watch Bailey talk as you go. Saved into the front desk's settings and
 * cached for Bailey's own conversations (decision 3: one character).
 */
// The server keeps 300 characters of the first line (conciergeRoute).
const OPENING_MAX = 300;

const reducedMotion = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } };

function Typing() {
    return <div className="soul-typing" role="status" aria-label="Bailey is typing"><span /><span /><span /></div>;
}

export default function SoulPage({ fd, look, salonName, motionPaused, onSaved, onCancel }) {
    const [soul, setSoul] = useState(() => normaliseSoul(fd.soul));
    // Their own first line, if they want one. Blank: I open in the soul's style.
    const [ownLine, setOwnLine] = useState(() => fd.settings.openingLine || '');
    const [seeded, setSeeded] = useState(fd.ready);
    const [tab, setTab] = useState('client');
    const [voicePanel, setVoicePanel] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const notesRef = useRef(null);
    const alive = useRef(true);
    const salon = salonName || 'your salon';

    // Opened before the settings arrived: take them once they do.
    useEffect(() => {
        if (seeded || !fd.ready) return;
        setSoul(normaliseSoul(fd.soul));
        setOwnLine(fd.settings.openingLine || '');
        setSeeded(true);
    }, [fd.ready, fd.soul, fd.settings.openingLine, seeded]);

    useEffect(() => () => { alive.current = false; }, []);

    // A starting point sets the dials, emoji and rules; what they wrote stays.
    const pick = (id) => setSoul((cur) => ({ ...fromPreset(id), notes: cur.notes }));
    const tune = (patch) => setSoul((cur) => {
        const next = normaliseSoul({ ...cur, ...patch, preset: cur.preset });
        // Dials that no longer match the starting point make it a tuned soul.
        next.preset = isTuned(next) ? presetFor(next) : next.preset;
        return next;
    });
    const toggleRule = (id) => setSoul((cur) => ({ ...cur, rules: cur.rules.includes(id) ? cur.rules.filter((r) => r !== id) : RULES.map((r) => r.id).filter((r) => r === id || cur.rules.includes(r)) }));

    // Old-page text can be longer than the notes allow: it's shown in full,
    // never cut on the quiet, and has to be shortened before it's saved.
    const over = Math.max(0, soul.notes.length - NOTES_MAX);
    const save = async () => {
        // Too long to save: take them to it rather than a dead button.
        if (over) {
            hapticError();
            const el = notesRef.current;
            if (el) { el.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' }); el.focus({ preventScroll: true }); }
            return;
        }
        setBusy(true); setError('');
        try {
            // Only the soul: the voice is saved by its own panel.
            await fd.save({ greeting: compileSoul(soul), openingLine: ownLine.replace(/\s+/g, ' ').trim() });
            hapticSuccess();
            // Left while it saved (the header's back): stay where they went.
            if (alive.current) onSaved();
        } catch (e) { if (!alive.current) return; hapticError(); setError(errorText(e, 'That didn’t save. Try again.')); setBusy(false); }
    };

    const hue = soulHue(soul);
    const compiled = compileSoul(soul);
    // A beat of typing before Bailey's reply whenever it changes.
    const [typing, setTyping] = useState(false);
    const reply = tab === 'you' ? ownerReply(soul) : clientReply(soul);
    useEffect(() => {
        if (tab === 'phone' || reducedMotion()) return undefined;
        setTyping(true);
        const t = setTimeout(() => setTyping(false), 480);
        return () => clearTimeout(t);
    }, [reply, tab]);
    const chosenSoul = SOULS.find((p) => p.id === soul.preset);
    const own = ownLine.replace(/\s+/g, ' ').trim();
    const chatFirst = own || chatGreeting(soul, salon);
    const phoneFirst = own || phoneGreeting(soul, salon);

    return (
        <div className="bly-home soul-page">
            {(error || fd.error) && <div className="bly-error" role="alert">{error || fd.error}</div>}
            <div className="soul-layout">
                <div className="soul-form">
                    <div className="soul-who">
                        <div className="soul-who-face"><BaileyStill appearance={look} face /></div>
                        <div>
                            <h1>Bailey’s soul</h1>
                            <p>How I talk to your clients, on chat and on the phone. And to you.</p>
                        </div>
                    </div>

                    <div className="soul-block">
                        <div className="soul-title">Start with a soul <small>You can tune it below</small></div>
                        <SoulCards value={soul.preset} onPick={pick} motion={!motionPaused} />
                        {!chosenSoul && <span className="soul-custom">Tuned by you. Pick a soul above to start again.</span>}
                    </div>

                    <div className="soul-block">
                        <div className="soul-title">Tune it</div>
                        <div className="bly-card soul-dials">
                            {DIAL_IDS.map((d) => (
                                <SoulDial key={d} id={`soul-${d}`} label={DIALS[d].label} words={DIALS[d].words} ends={DIALS[d].ends} value={soul[d]} onChange={(v) => tune({ [d]: v })} />
                            ))}
                            <SoulSegment id="soul-emoji" label="Emoji in chat" hint="Never on the phone" options={EMOJI.words} value={soul.emoji} onChange={(i) => tune({ emoji: i })} />
                        </div>
                    </div>

                    <div className="soul-block">
                        <div className="soul-title">House rules <small>Optional</small></div>
                        <div className="soul-chips">
                            {RULES.map((r) => {
                                const on = soul.rules.includes(r.id);
                                return (
                                    <button key={r.id} type="button" aria-pressed={on} onClick={() => { hapticTick(); toggleRule(r.id); }}>
                                        {on ? <Icon.Tick size={13} /> : <Icon.Plus size={13} />}<span>{r.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="soul-block">
                        <label className="soul-title" htmlFor="soul-notes">Anything else? <small>Optional</small></label>
                        <textarea
                            id="soul-notes"
                            ref={notesRef}
                            className="fd-input soul-notes"
                            maxLength={Math.max(NOTES_MAX, soul.notes.length)}
                            rows={3}
                            placeholder="e.g. Call our stylists artists. Never say cheap."
                            value={soul.notes}
                            aria-invalid={over > 0}
                            onChange={(e) => {
                                // Read now: React 16 reuses the event, so it's empty inside the updater.
                                const v = e.target.value;
                                setSoul((cur) => ({ ...cur, notes: v.slice(0, Math.max(NOTES_MAX, cur.notes.length)) }));
                            }}
                        />
                        {over
                            ? <span className="fd-hint is-over" role="alert">This came from your old settings and is {over} {over === 1 ? 'character' : 'characters'} too long. Shorten it to save.</span>
                            : <span className="fd-hint">How I should talk, in {NOTES_MAX} characters. Facts like parking go in What I know.</span>}
                    </div>

                    <div className="soul-block">
                        <label className="soul-title" htmlFor="soul-first">Your own first line? <small>Optional</small></label>
                        <input
                            id="soul-first"
                            type="text"
                            className="fd-input"
                            maxLength={OPENING_MAX}
                            placeholder="Leave blank and I’ll say hello in your soul’s style"
                            value={ownLine}
                            onChange={(e) => setOwnLine(e.target.value.slice(0, OPENING_MAX))}
                        />
                        <span className="fd-hint">The first thing I say in the chat and on the phone. If it doesn’t say I’m an AI assistant, I add that.</span>
                    </div>
                </div>

                <div className="soul-preview" aria-live="polite">
                    <div className="soul-tabs" role="tablist" aria-label="Preview">
                        {[['client', 'With a client'], ['phone', 'On the phone'], ['you', 'With you']].map(([id, label]) => (
                            <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => { hapticTick(); setTab(id); }}>{label}</button>
                        ))}
                    </div>
                    <div className="soul-chat">
                        <div className="soul-chat-head">
                            <div className="soul-chat-face"><BaileyStill appearance={look} face /></div>
                            <div><b>Bailey</b><small>{tab === 'phone' ? `Answering ${fd.settings.voiceNumber ? 'your number' : 'the phone'}` : tab === 'you' ? 'In the Bailey tab' : `AI assistant · ${salon}`}</small></div>
                        </div>
                        {tab === 'phone' ? (
                            <div className="soul-call">
                                <span className="soul-call-ring"><Icon.Phone size={24} /></span>
                                <q key={`${compiled}|${own}`}>{phoneFirst}</q>
                                <small>The first thing callers hear</small>
                                <VoiceLine fd={fd} onChange={() => setVoicePanel(true)} />
                            </div>
                        ) : (
                            // Keyed by what each message says, so only a message that
                            // changed comes in again; Bailey types before each new reply.
                            <div className="soul-chat-body" key={tab}>
                                {tab === 'client' ? (
                                    <>
                                        <div key={`g:${chatFirst}`} className="soul-bub is-them">{chatFirst}</div>
                                        <div key="q" className="soul-bub is-me">Hi! Have you got anything on Friday for a cut and blow dry?</div>
                                        {typing ? <Typing key="t" /> : <div key={`r:${clientReply(soul)}`} className="soul-bub is-them">{clientReply(soul)}</div>}
                                    </>
                                ) : (
                                    <>
                                        <div key="q" className="soul-bub is-me">How did we do last week?</div>
                                        {typing ? <Typing key="t" /> : <div key={`r:${ownerReply(soul)}`} className="soul-bub is-them">{ownerReply(soul)}</div>}
                                    </>
                                )}
                            </div>
                        )}
                        <div className="soul-foot"><span className="soul-dot" style={{ background: hue }} /><span>{soulSummary(soul)}</span></div>
                    </div>
                    <p className="fd-hint">Example booking and figures. The real Bailey uses your prices, free times and takings.</p>
                </div>
            </div>

            {voicePanel && <VoicePanel fd={fd} onClose={() => setVoicePanel(false)} />}

            {/* Pinned to the bottom, with the app's own footer buttons. */}
            <div className="soul-save">
                <button type="button" className="sp-btn-secondary" disabled={busy} onClick={() => { hapticTick(); onCancel(); }}>Cancel</button>
                <button type="button" className="sp-btn-primary" disabled={busy || !fd.ready} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
        </div>
    );
}
